import classNames from 'classnames';
import React from 'react';

import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import {
  CloseableDropdownContext,
  CloseableDropdownContextType,
} from 'ui-library/dropdowns/CloseableDropdownContext';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import * as keyboard from 'util/Keyboard';

/**
 * Close a dropdown component.
 *
 * This is to support closing the dropdown on select. The Options take the onSelect and are children of
 * Dropdown, but because they are passed as JSX as part of the Dropdown menu, the Dropdown cannot intercept
 * the onSelect.
 *
 * Ideally, the dropdown state would be managed in redux, but for now we are surfacing an action-like
 * function to close the dropdown.
 */
export function closeDropdown(dropdown: React.Component<DropdownProps>): void {
  dropdown.setState({isOpen: false});
}

export interface DropdownProps {
  // The toggle (a button, text, etc.)
  children: JSX.Element;
  className?: string | null;
  closeOnInsideClick?: boolean;
  closeOnOutsideClick?: boolean;
  // A <Menu /> component.
  menu: JSX.Element | null;
  // Can be controlled by the parent; otherwise, open state is changed from a click toggle.
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  canDropUnder?: boolean;
  alwaysDropDown?: boolean;
  alwaysDropUp?: boolean;
  tooltip?: string | null;
  /**
   * When this prop is true, there will be no backdrop behind the dropdown. This means that click events
   * outside the dropdown will still cause interactions with the rest of the page.
   *
   * Note that if noBackdrop is set to true, the dropdown will not close on outside click unless
   * closeOnOutsideClick is also true.
   */
  noBackdrop?: boolean;
  /**
   * When this prop is true, the passed in menu will be wrapped with the CloseableDropdownContext,
   * which allows the menu to close the Dropdown. This only works when the menu is or includes
   * a SearchableMenu, but not when it is a plain Menu. Default to true. The best practice is to
   * set it to false when menu does not include SearchableMenu, or the SearchableMenu has no submenu.
   *
   * Adding the wrapper can sometimes mess up the maxHeight calculation inside AbsoluteDropdownContainer.
   * Set it to false if there are weird browser test failures.
   */
  allowMenuToCloseDropdown?: boolean;
}

interface DropdownState {
  isOpen: boolean;
}

/**
 * Warning: Dropdown requires its child component to pass on an `onClick` property
 *
 * This dropdown components should support all dropdown use cases.
 * It uses AbsoluteDropdownContainer to render the dropdown menu on body to ensure correct positioning.
 * It takes a <Menu /> component that is controlled by the component toggle.
 *
 * See go/storybook/?path=/story/ui-library-dropdown--basic for example usages.
 */
export default class Dropdown extends React.Component<DropdownProps, DropdownState> {
  override state: DropdownState = {
    isOpen: false,
  };

  static defaultProps = {
    closeOnInsideClick: true,
    closeOnOutsideClick: true,
    canDropUnder: false,
    alwaysDropDown: false,
    alwaysDropUp: false,
    allowMenuToCloseDropdown: true,
  };

  _isOpen(): boolean {
    return this.props.isOpen != null ? this.props.isOpen : this.state.isOpen;
  }

  _doToggle(isOpen: boolean): void {
    if (this.props.onToggle) {
      this.props.onToggle(isOpen);
    } else {
      this.setState({isOpen});
    }
  }

  _handleToggle = (e: Event): void => {
    // Prevent RichObjectTable header from collapsing
    e.stopPropagation?.();
    this._doToggle(!this._isOpen());
  };

  _handleOutsideClick = (): void => {
    if (this.props.closeOnOutsideClick) {
      this._doToggle(false);
    }
  };

  closeableDropdownContextValue: CloseableDropdownContextType = {
    onCloseDropdown: this._handleOutsideClick,
  };

  _wrapMenuWithContext(menu: JSX.Element | null, allowMenuToCloseDropdown?: boolean): JSX.Element | null {
    if (menu != null && allowMenuToCloseDropdown) {
      return (
        <CloseableDropdownContext.Provider value={this.closeableDropdownContextValue}>
          {menu}
        </CloseableDropdownContext.Provider>
      );
    }
    return menu;
  }

  override render(): JSX.Element {
    const toggle = <DropdownToggle onClick={this._handleToggle}>{this.props.children}</DropdownToggle>;
    return (
      <AbsoluteDropdownContainer
        className={classNames('dropdownComponent', this.props.className)}
        menu={this.props.menu}
        onRequestClose={this._handleOutsideClick}
        open={this._isOpen()}
        shouldCloseOnInsideClick={this.props.closeOnInsideClick}
        shouldCloseOnOutsideClick={this.props.closeOnOutsideClick}
        noBackdrop={this.props.noBackdrop}
        canDropUnder={this.props.canDropUnder}
        alwaysDropDown={this.props.alwaysDropDown}
        alwaysDropUp={this.props.alwaysDropUp}
        wrapMenuWithContext={(menu) => this._wrapMenuWithContext(menu, this.props.allowMenuToCloseDropdown)}
      >
        {this.props.tooltip ? (
          <Tooltip text={this.props.tooltip} disableContentSelection={true}>
            {toggle}
          </Tooltip>
        ) : (
          toggle
        )}
      </AbsoluteDropdownContainer>
    );
  }
}

interface DropdownToggleProps {
  children: JSX.Element;
  onClick: (e: Event) => void;
}

/**
 * A light wrapper around the toggle to make the dropdown an uncontrolled component.
 */
export class DropdownToggle extends React.Component<DropdownToggleProps> {
  _handleKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    const toggle = React.Children.only(this.props.children);
    const originalOnKeyDown = toggle.props.onKeyDown;
    if (originalOnKeyDown) {
      originalOnKeyDown(e);
    }
    if (keyboard.isDownKey(e) && !e.defaultPrevented) {
      e.preventDefault();
      this.props.onClick(e.nativeEvent);
    }
  };

  override render(): JSX.Element {
    const toggle = React.Children.only(this.props.children);
    const onClick = toggle.props.disabled ? null : toggle.props.onClick || this.props.onClick;
    const onKeyDown = toggle.props.disabled ? null : this._handleKeyDown;
    return React.cloneElement(toggle, {onClick, onKeyDown});
  }
}
